import * as React from "react";
import { useState } from "react";
import PropTypes from "prop-types";
import { Button, makeStyles, mergeClasses } from "@fluentui/react-components";
import {
  Navigation24Regular,
  DismissCircle24Regular,
  Info24Regular,
  SignOut24Regular,
  Library24Regular,
} from "@fluentui/react-icons";


const styles = {
  menuContainer: {
    position: 'relative',
    display: 'inline-block',
  },
  menuList: {
    position: 'absolute',
    top: '100%',
    left: 0,
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.1)',
    zIndex: 100,
    minWidth: '200px',
    padding: '4px',
    listStyle: 'none',
    margin: 0,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    borderRadius: '4px',
    fontSize: '14px',
    outline: 'none',
  },
};

const useStyles = makeStyles({
  menuButton: {
    minWidth: "auto",
    border: "2px solid #fff",
    color: "#fff",
    maxWidth: "30px",
    padding: "3px",
    transition: 'background-color 0.2s ease, color 0.2s ease',
    ":hover": {
      backgroundColor: "#fff",
      color: "#000",
    },
  },
  // This class will be ADDED when the menu is open
  menuButtonActive: {
    backgroundColor: "#fff",
    color: "#000",
  },
});

/**
 * HoverMenu Component
 *
 * A dropdown menu triggered by hovering over a button.
 * Provides quick access to application-wide actions like clearing chat, viewing info,
 * accessing the prompt library, and logging out.
 *
 * @param {function} onClearChat - Callback to clear the current chat session
 * @param {function} onShowInfo - Callback to show information (currently opens a URL)
 * @param {function} onLogout - Callback to log the user out
 * @param {function} onShowPromptLibrary - Callback to open the prompt library modal
 * @param {boolean} isLoading - Disables the menu interaction if true
 * @param {boolean} isUploading - Disables the menu interaction if true
 */
const HoverMenu = ({ onClearChat, onShowInfo, onLogout, onShowPromptLibrary, isLoading, isUploading }) => {
  const componentStyles = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  // A timer to add a small delay before closing the menu.
  let closeTimer;

  /**
* Handles the mouse entering the menu area.
* Opens the menu and clears any pending close timer.
*/
  const handleMouseEnter = () => {
    // If loading, do not allow the menu to open
    if (isLoading) return;
    clearTimeout(closeTimer); // Clear any pending close timer
    setIsOpen(true);
  };

  /**
* Handles the mouse leaving the menu area.
* Sets a timer to close the menu after a short delay (200ms) to improve UX.
*/
  const handleMouseLeave = () => {
    // Set a timer to close the menu after a short delay (e.g., 200ms)
    closeTimer = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  /**
    * Generic handler for menu item clicks.
    * Closes the menu immediately and executes the passed action.
    * @param {function} action - The specific action function to execute
    */
  const handleItemClick = (action) => {
    setIsOpen(false);
    action();
  };

  /**
   * Specific handler for the Info menu item.
   * Opens an external Google Drive link in a new tab.
   */
  const handleInfoClick = () => {
    setIsOpen(false);
    window.open("https://drive.google.com/file/d/1uDvrGqyA0t2ZVL7rNc8MLr60WY2hxXgW/view", "_blank", "noopener,noreferrer");
  };

  return (
    <div
      style={styles.menuContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* This is the trigger button */}
      <Button
        appearance="transparent"
        icon={<Navigation24Regular />}
        disabled={isLoading || isUploading}
        aria-haspopup="true"
        aria-expanded={isOpen}
        className={mergeClasses(
          componentStyles.menuButton,
          isOpen && componentStyles.menuButtonActive
        )}
      />

      {/* The menu list is only rendered if `isOpen` is true and not loading. */}
      {isOpen && !isLoading && !isUploading && (
        <ul style={styles.menuList}>
          {/* Info Item */}
          <li
            style={styles.menuItem}
            className="hover-menu-item"
            onClick={handleInfoClick}
            onKeyDown={(e) => e.key === 'Enter' && handleInfoClick()}
            tabIndex={0}
          >
            <Info24Regular />
            <span>Info</span>
          </li>

          {/* Clear Chat Item */}
          <li
            style={styles.menuItem}
            className="hover-menu-item"
            onClick={() => handleItemClick(onClearChat)}
            onKeyDown={(e) => e.key === 'Enter' && handleItemClick(onClearChat)}
            tabIndex={0}
          >
            <DismissCircle24Regular />
            <span>Clear Chat</span>
          </li>

          {/* Prompt Library Item */}
          <li
            style={styles.menuItem}
            className="hover-menu-item"
          onClick={() => handleItemClick(onShowPromptLibrary)}
          onKeyDown={(e) => e.key === 'Enter' && handleItemClick(onShowPromptLibrary)}
          tabIndex={0} 
          >
          <Library24Regular />
          <span>Prompt Library</span>
        </li>

          {/* Logout Item */}
      <li
        style={styles.menuItem}
        className="hover-menu-item"
        onClick={() => handleItemClick(onLogout)}
        onKeyDown={(e) => e.key === 'Enter' && handleItemClick(onLogout)}
        tabIndex={0}
      >
        <SignOut24Regular />
        <span>Logout</span>
      </li>
    </ul>
  )
}
    </div >
  );
};

HoverMenu.propTypes = {
  onClearChat: PropTypes.func.isRequired,
  onShowInfo: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  onShowPromptLibrary: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default HoverMenu;